import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, Clock, Pencil, Trash2 } from "lucide-react";
import NewsletterForm from "@/components/NewsletterForm";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
}

export default function ArticleDetail() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api
      .get(`/articles/${slug}`)
      .then(({ data }) => setArticle(data))
      .catch(() => setNotFound(true));
  }, [slug]);

  const remove = async () => {
    if (!window.confirm("Bu yazıyı silmek istediğine emin misin?")) return;
    try {
      await api.delete(`/articles/${article.article_id}`);
      toast.success("Yazı silindi");
      navigate("/yazilar");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Silinemedi");
    }
  };

  if (notFound) {
    return (
      <div className="k-container py-24 text-center">
        <h1 className="font-display text-5xl text-ink-900">Yazı bulunamadı</h1>
        <Link to="/yazilar" className="k-btn-secondary mt-6 inline-flex">
          Yazılara dön
        </Link>
      </div>
    );
  }
  if (!article)
    return (
      <div className="k-container py-16">
        <div className="k-card h-96 animate-pulse" />
      </div>
    );

  return (
    <>
      <article className="k-container py-12 md:py-16">
        <div className="mx-auto max-w-3xl">
          <div className="flex items-center justify-between">
            <Link to="/yazilar" className="k-btn-ghost text-sm" data-testid="back-to-articles">
              <ArrowLeft className="mr-1 h-4 w-4" /> Yazılar
            </Link>
            {user?.role === "admin" && (
              <div className="flex gap-2">
                <Link
                  to={`/yazilar/${article.slug}/duzenle`}
                  className="k-btn-ghost text-sm"
                  data-testid="edit-article-btn"
                >
                  <Pencil className="mr-1 h-4 w-4" /> Düzenle
                </Link>
                <button
                  onClick={remove}
                  className="k-btn-ghost text-sm text-destructive"
                  data-testid="delete-article-btn"
                >
                  <Trash2 className="mr-1 h-4 w-4" /> Sil
                </button>
              </div>
            )}
          </div>

          <div className="mt-6 flex items-center gap-3 text-sm text-ink-500">
            <span>{formatDate(article.published_at)}</span>
            <span className="inline-flex items-center gap-1">
              <Clock className="h-4 w-4" /> {article.read_minutes} dk okuma
            </span>
            <span>·</span>
            <span>{article.author_name}</span>
          </div>

          <h1 className="mt-3 font-display text-5xl leading-tight text-ink-900 md:text-6xl" data-testid="article-title">
            {article.title}
          </h1>
          <p className="mt-4 font-body text-lg text-ink-700" data-testid="article-summary">
            {article.summary}
          </p>

          {article.tags?.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {article.tags.map((t) => (
                <span key={t} className="k-tag">{t}</span>
              ))}
            </div>
          )}

          {article.cover_image_url && (
            <img
              src={article.cover_image_url}
              alt={article.title}
              className="mt-10 w-full rounded-[2rem_1rem_2rem_1rem] object-cover shadow-soft"
              style={{ maxHeight: 520 }}
            />
          )}

          <div
            className="k-prose mt-10"
            data-testid="article-body"
            dangerouslySetInnerHTML={{ __html: article.body }}
          />

          <div className="mt-16">
            <NewsletterForm source={`article-${article.slug}`} />
          </div>
        </div>
      </article>
    </>
  );
}
